import { valueIsDefined } from "@legacy/core/utils/utils"
import clsx from "clsx"

import { faIcon } from "@utils/faIcon"

import { SkeletonTypography, TruncatableText } from "@particles/index"

import { Icon, IconButton } from "@atoms"

import styles from "@molecules/ValueDisplay/ValueDisplay.module.scss"
import type { ValueDisplayHorizontalProps } from "@molecules/ValueDisplay/ValueDisplay.types"

export default function HorizontalValueDisplay(props: ValueDisplayHorizontalProps) {
    const {
        className,
        label,
        showLabel = true,
        value,
        valueType = "text",
        icon,
        showIcon = true,
        actionButton,
        isWarning = false,
        valueColor,
        isLoading = false,
    } = props

    const hasValue = valueIsDefined(value)
    const placeholder = `No ${label}`

    // When an icon/avatar is present the value area is single-line. A plain-text
    // value truncates and reveals the full string in a cursor-anchored tooltip on
    // hover — only when it actually overflows — matching Badge/LinkButton behaviour.
    const hasLeftElement = showIcon && !!icon
    const isTextValue = typeof value === "string"

    const warningIcon = faIcon({ name: "triangle-exclamation", style: "solid" })

    const valueColorClass = isWarning
        ? styles.warningValue
        : valueColor
          ? styles[`${valueColor}ValueColor`]
          : undefined

    return (
        <div className={clsx(styles.base, styles.horizontal, className)}>
            {showLabel && <div className={styles.label}>{label}</div>}

            <div className={clsx(styles.valueArea, { [styles.badgeValueArea]: valueType === "badge" })}>
                {isLoading ? (
                    <SkeletonTypography variant="bodyCompact" />
                ) : (
                    <div className={clsx(styles.valueRow, { [styles.warningRow]: isWarning })}>
                        {hasLeftElement && <div className={styles.leftElement}>{icon}</div>}

                        {hasLeftElement && hasValue && isTextValue ? (
                            <TruncatableText
                                className={clsx(styles.value, styles.truncatedValue, valueColorClass)}
                                text={value}
                                tooltipOnTruncate={true}
                                tooltipPlacement="top"
                            />
                        ) : (
                            <div
                                className={clsx(
                                    styles.value,
                                    valueColorClass,
                                    hasLeftElement && styles.truncatedValue,
                                    !hasValue && styles.placeholderValue,
                                )}
                            >
                                {hasValue ? value : placeholder}
                            </div>
                        )}

                        {isWarning && (
                            <div className={styles.warningIconWrapper}>
                                <Icon icon={warningIcon} size={14} className={styles.warningIcon} />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!isLoading && actionButton && (
                <div className={styles.actionButtonWrapper}>
                    <IconButton {...actionButton} size="lg" variant="muted" />
                </div>
            )}
        </div>
    )
}
